import { getPlatforms, isPlatform } from '@ionic/react';
import { browserVersion, isChrome, isEdge, isSafari } from 'react-device-detect';
import { doc, setDoc, getDoc, query, collection, where, getDocs, deleteDoc, writeBatch, DocumentData, QuerySnapshot, WriteBatch } from 'firebase/firestore';
import { firebaseDB } from '../components/firebase/firebase';
import { logoutWithCleanup } from '../components/firebase/firebase';

console.log('browserVersion ', browserVersion);

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface FontConfig {
  name: string;
  url: string;
  style: 'normal' | 'italic' | 'oblique';
  weight: 'normal' | 'bold' | number;
}

interface UserProfile {
  displayName?: string;
  companyName?: string;
  role?: string;
  email?: string;
  updatedAt?: Date;
  [key: string]: any;
}

interface ProfileData {
  displayName: string;
  companyName: string;
  role: string;
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

interface DeleteAccountResult {
  success: boolean;
  message: string;
}

interface PopupOptions {
  url: string;
  title: string;
  width: number;
  height: number;
}

interface KnowledgeBaseDocument {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: Date;
}

// ============================================================================
// BROWSER DETECTION CONSTANTS
// ============================================================================

const safariVersion: number = 12;
const chromeVersion: number = 120;
const supportedChrome: boolean = isChrome && parseInt(browserVersion) >= chromeVersion;
const supportedSafari: boolean = isSafari && parseInt(browserVersion) >= safariVersion;
const isEdgeBrowser: boolean = isEdge && parseInt(browserVersion) >= chromeVersion;
const isBraveBrowser: boolean = Boolean((navigator as any)?.brave);
const platforInfo: string[] = getPlatforms();
const isDesktopApp: boolean = platforInfo.includes('electron');
const isCapacitor: boolean = isPlatform('capacitor') && !isDesktopApp;

// ============================================================================
// IFRAME UTILITIES
// ============================================================================

const inIframe = (): boolean => {
  //return true;
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const isExtenstionSidebar = (): boolean => {
  return inIframe() && window.name === 'sidebar';
};

const isOffscreenIframe = (): boolean => {
  return inIframe() && window.name === 'offscreen';
};

// todo: make this secure
const postIframeMessage = (data: any): void => {
  try {
    if (window.top && window.top !== window) {
      window.top.postMessage(data, '*');
    }
  } catch (error) {
    console.warn('Failed to post iframe message:', error);
  }
};

const inAppBrowser: boolean = navigator.userAgent.includes('Instagram');

(window as any).platforInfo = platforInfo;

const isChromium: boolean = !!(navigator as any).userAgentData && (navigator as any).userAgentData.brands.some((data: any) => data.brand == 'Chromium');
const isSupportedBrowser: boolean =
  supportedChrome ||
  supportedSafari ||
  isCapacitor ||
  inAppBrowser ||
  isBraveBrowser ||
  isEdgeBrowser ||
  isDesktopApp ||
  isChromium;
const isCalendarViewOnly: boolean = isExtenstionSidebar() || isCapacitor || isDesktopApp;

// ============================================================================
// FONT LOADING UTILITIES
// ============================================================================

import wotfardRegular from '../fonts/wotfard-regular-webfont.woff2';
import wotfardSemibold from '../fonts/wotfard-semibold-webfont.woff2';
import wotfardMedium from '../fonts/wotfard-medium-webfont.woff2';
import wotfardBold from '../fonts/Wotfard-Bold.otf';

const fonts: FontConfig[] = [
  {
    name: 'wotfardregular',
    url: wotfardRegular,
    style: 'normal',
    weight: 'normal'
  },
  {
    name: 'wotfardsemibold',
    url: wotfardSemibold,
    style: 'normal',
    weight: 600
  },
  {
    name: 'wotfardmedium',
    url: wotfardMedium,
    style: 'normal',
    weight: 600
  },
  {
    name: 'wotfardbold',
    url: wotfardBold,
    style: 'normal',
    weight: 'bold'
  }
];

async function loadFonts(): Promise<void> {
  console.log('loadFonts: loading fonts');
  const result = await Promise.allSettled(
    fonts.map(async (item: FontConfig) => {
      const { style, weight, name, url } = item;
      const font = new FontFace(name, `url(${url})`, { style, weight: weight.toString() });
      await font.load();
      return name;
    })
  );
  console.log('loadFonts: result ', result);
}

// ============================================================================
// POPUP UTILITIES
// ============================================================================

export function openCenteredPopup(url: string, title: string, w: number, h: number): Window | null {
  const dualScreenLeft: number = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop: number = window.screenTop !== undefined ? window.screenTop : window.screenY;
  const width: number = window.innerWidth || document.documentElement.clientWidth || screen.width;
  const height: number = window.innerHeight || document.documentElement.clientHeight || screen.height;
  const systemZoom: number = width / window.screen.availWidth;
  const left: number = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top: number = (height - h) / 2 / systemZoom + dualScreenTop;
  const newWindow: Window | null = window.open(
    url,
    title,
    `scrollbars=yes,width=${w / systemZoom},height=${h / systemZoom},top=${top},left=${left},resizable=yes,toolbar=no,menubar=no,status=no`
  );
  if (newWindow) newWindow.focus();
  return newWindow;
}

// ============================================================================
// PROFILE UTILITIES
// ============================================================================

/**
 * Check if user profile is incomplete (missing companyName or role)
 */
export const isProfileIncomplete = (userData: UserProfile | null): boolean => {
  if (!userData) return true;
  
  return !userData.companyName || 
         !userData.role || 
         userData.companyName.trim() === '' || 
         userData.role.trim() === '';
};

/**
 * Get user data from Firebase
 */
export const getUserProfileFromFirebase = async (userEmail: string): Promise<UserProfile | null> => {
  try {
    // First check in gloqal-moderators collection (by email)
    const moderatorRef = doc(firebaseDB, 'gloqal-moderators', userEmail);
    const moderatorSnap = await getDoc(moderatorRef);
    
    if (moderatorSnap.exists()) {
      return moderatorSnap.data() as UserProfile;
    }

    // Fallback to users collection (by email query)
    const usersRef = doc(firebaseDB, 'users', userEmail);
    const userSnap = await getDoc(usersRef);
    
    if (userSnap.exists()) {
      return userSnap.data() as UserProfile;
    }

    return null;
  } catch (error) {
    console.error('Error getting user profile from Firebase:', error);
    return null;
  }
};

/**
 * Update user profile with display name, company name and role
 */
export const updateUserProfile = async (userEmail: string, profileData: ProfileData): Promise<void> => {
  try {
    const updateData: UserProfile = {
      displayName: profileData.displayName,
      companyName: profileData.companyName,
      role: profileData.role,
      updatedAt: new Date()
    };

    // Update in gloqal-moderators collection (primary)
    const moderatorRef = doc(firebaseDB, 'gloqal-moderators', userEmail);
    await setDoc(moderatorRef, updateData, { merge: true });

    // Also update in users collection for consistency
    const userRef = doc(firebaseDB, 'users', userEmail);
    await setDoc(userRef, updateData, { merge: true });

    console.log('[AppUtils] User profile updated successfully');
  } catch (error) {
    console.error('[AppUtils] Error updating user profile:', error);
    throw error;
  }
};

/**
 * Check if user has seen the incomplete profile modal
 */
export const hasSeenIncompleteProfileModal = (userEmail: string): boolean => {
  const key: string = `incompleteProfileModalShown_${userEmail}`;
  return localStorage.getItem(key) === 'true';
};

/**
 * Mark that user has seen the incomplete profile modal
 */
export const markIncompleteProfileModalAsSeen = (userEmail: string): void => {
  const key: string = `incompleteProfileModalShown_${userEmail}`;
  localStorage.setItem(key, 'true');
};

/**
 * Firestore-backed flag: has the user completed onboarding (incomplete profile modal)?
 * Uses Firestore as source of truth with localStorage fallback for legacy behavior.
 */
export const getOnboardingSeenFromFirestore = async (userEmail: string) => {
  try {
    if (!firebaseDB || !userEmail) {
      return hasSeenIncompleteProfileModal(userEmail);
    }
    const userRef = doc(firebaseDB, 'users', userEmail);
    const snap = await getDoc(userRef);
    const seen = snap.exists() ? Boolean(snap.data()?.onboardingCompleted) : false;
    // mirror to localStorage to avoid flicker next loads
    if (seen) {
      markIncompleteProfileModalAsSeen(userEmail);
    }
    return seen;
  } catch (e) {
    return hasSeenIncompleteProfileModal(userEmail);
  }
};

/**
 * Mark onboarding as completed in Firestore and mirror to localStorage.
 */
export const setOnboardingSeenInFirestore = async (userEmail: string) => {
  try {
    if (firebaseDB && userEmail) {
      const userRef = doc(firebaseDB, 'users', userEmail);
      await setDoc(userRef, { onboardingCompleted: true, onboardingCompletedAt: new Date() }, { merge: true });
    }
  } finally {
    // Always set local flag as well
    markIncompleteProfileModalAsSeen(userEmail);
  }
};

/**
 * Check if a clone URL is available in Firebase
 * @param cloneUrl - The clone URL to check
 * @param userEmail - The current user's email (to allow them to keep their own URL)
 * @returns Promise<boolean> - true if available, false if taken
 */
export const checkCloneUrlAvailability = async (cloneUrl: string, userEmail?: string): Promise<boolean> => {
  try {
    if (!firebaseDB) {
      console.warn('Firebase not configured, cannot check clone URL availability');
      return false;
    }

    // Clean the URL (remove any protocol or domain)
    const cleanUrl: string = cloneUrl
      .replace(/^https?:\/\//, '') // Remove http:// or https://
      .replace(/^www\./, '') // Remove www.
      .replace(/^myclone\.cc\//, '') // Remove myclone.cc/ prefix
      .replace(/\/$/, '') // Remove trailing slash
      .toLowerCase()
      .trim();

    if (!cleanUrl) {
      return false;
    }

    // Check if the URL exists in user-clones collection
    const cloneRef = doc(firebaseDB, 'user-clones', cleanUrl);
    const cloneDoc = await getDoc(cloneRef);

    if (!cloneDoc.exists()) {
      return true; // URL is available
    }

    // If userEmail is provided, check if this is the user's own URL
    if (userEmail) {
      const cloneData = cloneDoc.data();
      if (cloneData?.email === userEmail) {
        return true; // User can keep their own URL
      }
    }

    return false; // URL is taken by another user
  } catch (error) {
    console.error('Error checking clone URL availability:', error);
    return false;
  }
};

/**
 * Validate clone URL format
 * @param cloneUrl - The clone URL to validate
 * @returns Object with isValid boolean and error message
 */
export const validateCloneUrl = (cloneUrl: string): ValidationResult => {
  if (!cloneUrl || cloneUrl.trim() === '') {
    return { isValid: false, error: 'Clone URL is required' };
  }

  const cleanUrl: string = cloneUrl
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/^myclone\.cc\//, '')
    .replace(/\/$/, '')
    .toLowerCase()
    .trim();

  if (cleanUrl.length < 2) {
    return { isValid: false, error: 'Clone URL must be at least 2 characters long' };
  }

  if (cleanUrl.length > 50) {
    return { isValid: false, error: 'Clone URL must be less than 50 characters' };
  }

  // Check for valid characters (alphanumeric, hyphens, underscores)
  const validPattern: RegExp = /^[a-z0-9-_]+$/;
  if (!validPattern.test(cleanUrl)) {
    return { isValid: false, error: 'Clone URL can only contain letters, numbers, hyphens, and underscores' };
  }

  // Check for reserved words
  const reservedWords: string[] = ['admin', 'api', 'app', 'www', 'mail', 'ftp', 'blog', 'support', 'help', 'about', 'contact', 'login', 'signup', 'register', 'dashboard', 'settings', 'profile', 'account', 'billing', 'payment', 'pricing', 'terms', 'privacy', 'legal', 'docs', 'documentation', 'status', 'news', 'press', 'media', 'careers', 'jobs', 'hiring', 'team', 'company', 'about', 'contact', 'support', 'help', 'faq', 'community', 'forum', 'discord', 'slack', 'twitter', 'facebook', 'linkedin', 'instagram', 'youtube', 'github', 'gitlab', 'bitbucket', 'stackoverflow', 'reddit', 'medium', 'dev', 'hashnode', 'substack', 'newsletter', 'blog', 'articles', 'posts', 'stories', 'tutorials', 'guides', 'docs', 'documentation', 'api', 'sdk', 'library', 'framework', 'tool', 'tools', 'resources', 'links', 'bookmarks', 'favorites', 'saved', 'bookmarked', 'liked', 'shared', 'recommended', 'featured', 'popular', 'trending', 'latest', 'new', 'recent', 'updated', 'modified', 'changed', 'version', 'release', 'changelog', 'roadmap', 'features', 'benefits', 'advantages', 'pros', 'cons', 'comparison', 'vs', 'versus', 'alternative', 'alternatives', 'competitor', 'competitors', 'competition', 'market', 'industry', 'sector', 'niche', 'category', 'tag', 'tags', 'label', 'labels', 'filter', 'filters', 'sort', 'order', 'search', 'query', 'result', 'results', 'page', 'pages', 'section', 'sections', 'part', 'parts', 'chapter', 'chapters', 'lesson', 'lessons', 'course', 'courses', 'tutorial', 'tutorials', 'guide', 'guides', 'howto', 'how-to', 'tips', 'tricks', 'hacks', 'cheats', 'secrets', 'best-practices', 'best-practice', 'patterns', 'design-patterns', 'architecture', 'structure', 'organization', 'management', 'administration', 'admin', 'moderator', 'mod', 'user', 'users', 'member', 'members', 'guest', 'guests', 'visitor', 'visitors', 'viewer', 'viewers', 'audience', 'audiences', 'customer', 'customers', 'client', 'clients', 'partner', 'partners', 'sponsor', 'sponsors', 'donor', 'donors', 'supporter', 'supporters', 'follower', 'followers', 'subscriber', 'subscribers', 'fan', 'fans', 'lover', 'lovers', 'enthusiast', 'enthusiasts', 'expert', 'experts', 'professional', 'professionals', 'specialist', 'specialists', 'consultant', 'consultants', 'advisor', 'advisors', 'mentor', 'mentors', 'coach', 'coaches', 'trainer', 'trainers', 'instructor', 'instructors', 'teacher', 'teachers', 'professor', 'professors', 'lecturer', 'lecturers', 'speaker', 'speakers', 'presenter', 'presenters', 'host', 'hosts', 'moderator', 'moderators', 'facilitator', 'facilitators', 'organizer', 'organizers', 'coordinator', 'coordinators', 'manager', 'managers', 'director', 'directors', 'executive', 'executives', 'ceo', 'cto', 'cfo', 'cmo', 'cpo', 'cso', 'coo', 'vp', 'vice-president', 'president', 'founder', 'founders', 'co-founder', 'co-founders', 'owner', 'owners', 'proprietor', 'proprietors', 'entrepreneur', 'entrepreneurs', 'businessman', 'businesswoman', 'businessperson', 'businesspeople', 'investor', 'investors', 'venture-capitalist', 'venture-capitalists', 'angel-investor', 'angel-investors', 'seed-investor', 'seed-investors', 'series-a', 'series-b', 'series-c', 'series-d', 'ipo', 'acquisition', 'merger', 'partnership', 'collaboration', 'cooperation', 'alliance', 'joint-venture', 'joint-ventures', 'subsidiary', 'subsidiaries', 'division', 'divisions', 'department', 'departments', 'unit', 'units', 'team', 'teams', 'group', 'groups', 'organization', 'organizations', 'company', 'companies', 'corporation', 'corporations', 'enterprise', 'enterprises', 'business', 'businesses', 'firm', 'firms', 'agency', 'agencies', 'consultancy', 'consultancies', 'studio', 'studios', 'lab', 'labs', 'laboratory', 'laboratories', 'research', 'development', 'r&d', 'innovation', 'technology', 'tech', 'digital', 'online', 'offline', 'virtual', 'remote', 'hybrid', 'cloud', 'saas', 'paas', 'iaas', 'api', 'sdk', 'library', 'libraries', 'framework', 'frameworks', 'platform', 'platforms', 'service', 'services', 'product', 'products', 'solution', 'solutions', 'tool', 'tools', 'software', 'hardware', 'device', 'devices', 'gadget', 'gadgets', 'app', 'apps', 'application', 'applications', 'program', 'programs', 'system', 'systems', 'database', 'databases', 'server', 'servers', 'hosting', 'host', 'domain', 'domains', 'website', 'websites', 'web', 'internet', 'www', 'http', 'https', 'ftp', 'smtp', 'pop', 'imap', 'dns', 'ip', 'tcp', 'udp', 'ssl', 'tls', 'ssh', 'vpn', 'lan', 'wan', 'wifi', 'bluetooth', 'nfc', 'rfid', 'qr', 'barcode', 'scanner', 'printer', 'monitor', 'keyboard', 'mouse', 'trackpad', 'touchpad', 'screen', 'display', 'projector', 'camera', 'microphone', 'speaker', 'headphone', 'headphones', 'earphone', 'earphones', 'earbud', 'earbuds', 'airpods', 'galaxy-buds', 'pixel-buds', 'sony-wh', 'bose', 'sennheiser', 'audio-technica', 'shure', 'akg', 'beyerdynamic', 'focal', 'grado', 'hifiman', 'audeze', 'fostex', 'denon', 'marantz', 'yamaha', 'pioneer', 'onkyo', 'harman', 'jbl', 'klipsch', 'b&w', 'bowers-wilkins', 'kef', 'elac', 'wharfedale', 'tannoy', 'monitor-audio', 'focal', 'dynaudio', 'b&o', 'bang-olufsen', 'devialet', 'naim', 'linn', 'rega', 'cambridge', 'arcam', 'rotel', 'nad', 'marantz', 'denon', 'yamaha', 'pioneer', 'onkyo', 'harman', 'jbl', 'klipsch', 'b&w', 'bowers-wilkins', 'kef', 'elac', 'wharfedale', 'tannoy', 'monitor-audio', 'focal', 'dynaudio', 'b&o', 'bang-olufsen', 'devialet', 'naim', 'linn', 'rega', 'cambridge', 'arcam', 'rotel', 'nad'];
  
  if (reservedWords.includes(cleanUrl)) {
    return { isValid: false, error: 'This URL is reserved and cannot be used' };
  }

  return { isValid: true };
};

/**
 * Get user's clone URL from Firebase
 * @param userEmail - The user's email address
 * @returns Promise<string | null> - The clone URL or null if not found
 */
export const getUserCloneUrlFromFirebase = async (userEmail: string): Promise<string | null> => {
  if (!firebaseDB) {
    console.warn('Firebase not configured, cannot get user clone URL from Firebase');
    return null;
  }

  try {
    // Query the user-clones collection for the user's clone
    const q = query(
      collection(firebaseDB, 'user-clones'),
      where('email', '==', userEmail),
      where('isUserClone', '==', true)
    );

    const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(q);

    if (!querySnapshot.empty) {
      // Get the first (and should be only) clone for this user
      const cloneDoc = querySnapshot.docs[0];
      const cloneData = cloneDoc.data();
      return cloneData.cloneUrl || null;
    }

    return null;
  } catch (error) {
    console.error('[AppUtils] Error getting user clone URL from Firebase:', error);
    return null;
  }
};

// ============================================================================
// ACCOUNT DELETION UTILITIES
// ============================================================================

/**
 * Delete all user data from all Firestore collections
 */
export const deleteUserAccount = async (userEmail: string): Promise<DeleteAccountResult> => {
  if (!firebaseDB) {
    return { success: false, message: 'Firebase not configured' };
  }

  try {
    console.log(`[AppUtils] Starting account deletion`);
    
    const batch: WriteBatch = writeBatch(firebaseDB);
    let deletedCount: number = 0;

    // Collections to clean up
    const collectionsToClean: string[] = [
      'users',
      'gloqal-moderators', 
      'user-clones',
      'userSchedulerSettings',
      'responses',
      'requests',
      'waitlist',
      'chats',
      'chat_messages',
      'public-calendar-availability'
    ];

    // Delete from each collection
    for (const collectionName of collectionsToClean) {
      try {
        console.log(`[AppUtils] Cleaning collection: ${collectionName}`);
        
        // Query documents where email matches userEmail
        const q = query(
          collection(firebaseDB, collectionName),
          where('email', '==', userEmail)
        );
        
        const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(q);
        
        // Add each document to batch delete
        querySnapshot.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
          deletedCount++;
        });

        // For userSchedulerSettings, also check by userEmail field
        if (collectionName === 'userSchedulerSettings') {
          const q2 = query(
            collection(firebaseDB, collectionName),
            where('userEmail', '==', userEmail)
          );
          
          const querySnapshot2: QuerySnapshot<DocumentData> = await getDocs(q2);
          querySnapshot2.forEach((docSnapshot) => {
            batch.delete(docSnapshot.ref);
            deletedCount++;
          });
        }

        // For public-calendar-availability, check by adminEmail
        if (collectionName === 'public-calendar-availability') {
          const q3 = query(
            collection(firebaseDB, collectionName),
            where('adminEmail', '==', userEmail)
          );
          
          const querySnapshot3: QuerySnapshot<DocumentData> = await getDocs(q3);
          querySnapshot3.forEach((docSnapshot) => {
            batch.delete(docSnapshot.ref);
            deletedCount++;
          });
        }

        console.log(`[AppUtils] Found ${querySnapshot.size} documents in ${collectionName}`);
        
      } catch (error) {
        console.error(`[AppUtils] Error cleaning collection ${collectionName}:`, error);
        // Continue with other collections even if one fails
      }
    }

    // Also delete user document by UID if we have it
    try {
      // Try to get user UID from localStorage
      const currentUserEmail: string | null = localStorage.getItem('currentUserEmail');
      if (currentUserEmail === userEmail) {
        // Try to delete by UID as well (users collection uses UID as document ID)
        const userRef = doc(firebaseDB, 'users', userEmail);
        batch.delete(userRef);
        deletedCount++;
      }
    } catch (error) {
      console.error('[AppUtils] Error deleting user by UID:', error);
    }

    // Execute batch delete
    if (deletedCount > 0) {
      await batch.commit();
      console.log(`[AppUtils] Successfully deleted ${deletedCount} documents`);
    } else {
      console.log('[AppUtils] No documents found to delete');
    }

    // Clear local storage
    localStorage.clear();
    sessionStorage.clear();
    
    // Also explicitly clear any user-specific keys that might not be caught by clear()
    const keysToRemove: string[] = [
      'welcomeModalShown',
      `incompleteProfileModalShown_${userEmail}`,
      'currentUserEmail',
      'adminEmail',
      'userDisplayName',
      'userProfile'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Log out the user
    await logoutWithCleanup('account-deleted');

    return { 
      success: true, 
      message: `Account successfully deleted. ${deletedCount} documents removed from database.` 
    };

  } catch (error) {
    console.error('[AppUtils] Error deleting user account:', error);
    return { 
      success: false, 
      message: `Failed to delete account: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
};

// ============================================================================
// STORAGE UTILITIES
// ============================================================================

/**
 * Safe storage wrapper for localStorage/sessionStorage with error handling
 * Prevents app crashes when storage operations fail (e.g., in private browsing)
 */
const safeStorage = {
  getItem: (storage: Storage, key: string): string | null => {
    try {
      return storage.getItem(key);
    } catch (error) {
      console.warn(`Storage getItem failed for key ${key}:`, error);
      return null;
    }
  },
  
  setItem: (storage: Storage, key: string, value: string): void => {
    try {
      storage.setItem(key, value);
    } catch (error) {
      console.warn(`Storage setItem failed for key ${key}:`, error);
      // Silently fail - don't break the app
    }
  },
  
  removeItem: (storage: Storage, key: string): void => {
    try {
      storage.removeItem(key);
    } catch (error) {
      console.warn(`Storage removeItem failed for key ${key}:`, error);
      // Silently fail - don't break the app
    }
  },
  
  clear: (storage: Storage): void => {
    try {
      storage.clear();
    } catch (error) {
      console.warn('Storage clear failed:', error);
      // Silently fail - don't break the app
    }
  }
};


/**
 * Safe localStorage wrapper with error handling
 * Prevents crashes when localStorage operations fail
 */
export const safeLocalStorage = {
  getItem: (key: string): string | null => safeStorage.getItem(localStorage, key),
  setItem: (key: string, value: string): void => safeStorage.setItem(localStorage, key, value),
  removeItem: (key: string): void => safeStorage.removeItem(localStorage, key),
  clear: (): void => safeStorage.clear(localStorage)
};

/**
 * Safe sessionStorage wrapper with error handling
 * Prevents crashes when sessionStorage operations fail
 */
export const safeSessionStorage = {
  getItem: (key: string): string | null => safeStorage.getItem(sessionStorage, key),
  setItem: (key: string, value: string): void => safeStorage.setItem(sessionStorage, key, value),
  removeItem: (key: string): void => safeStorage.removeItem(sessionStorage, key),
  clear: (): void => safeStorage.clear(sessionStorage)
};

// ============================================================================
// KNOWLEDGE BASE FUNCTIONS
// ============================================================================

/**
 * Get knowledge base documents for a clone
 */
export const getCloneKnowledgeBase = async (cloneUrl: string): Promise<KnowledgeBaseDocument[]> => {
  try {
    const cloneRef = doc(firebaseDB, 'user-clones', cloneUrl);
    const cloneSnap = await getDoc(cloneRef);
    
    if (cloneSnap.exists()) {
      const cloneData = cloneSnap.data();
      return cloneData.knowledgeBase || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error retrieving clone knowledge base:', error);
    return [];
  }
};

/**
 * Get knowledge base context for AI conversations
 */
export const getKnowledgeBaseContext = async (cloneUrl: string): Promise<string> => {
  try {
    const documents = await getCloneKnowledgeBase(cloneUrl);
    
    if (documents.length === 0) {
      return '';
    }
    
    // Create a context string with document information
    const documentList = documents.map(doc => 
      `- ${doc.name} (${doc.type}, uploaded ${doc.uploadedAt.toLocaleDateString()})`
    ).join('\n');
    
    return `The user has uploaded the following documents to their knowledge base:\n${documentList}\n\nWhen answering questions, you can reference information from these documents if relevant.`;
  } catch (error) {
    console.error('Error generating knowledge base context:', error);
    return '';
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export {
  isBraveBrowser,
  isDesktopApp,
  isOffscreenIframe,
  isSupportedBrowser,
  safariVersion,
  chromeVersion,
  isCapacitor,
  platforInfo,
  inAppBrowser,
  inIframe,
  isExtenstionSidebar,
  postIframeMessage,
  isCalendarViewOnly,
  loadFonts
};
